import React, { useState, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import styled from "@emotion/styled";
import { MDSLink } from "@/mds/components/text";
import { ErrorBoundaryProps } from "./mds-error-boundary.types";

/**
 * Generic error boundary with automatic retries for DOM manipulation errors.
 *
 * NOTE: This is technically NOT a MDS component but it's used in MDS
 * and is therefore included here.
 *
 * @param boundaryId - Unique identifier for this error boundary instance
 * @param errorMessage - Error message to display when error occurs
 * @param retryText - Text for the retry link
 * @param logPrefix - Prefix for console logging messages
 */
export const MDSErrorBoundary: React.FC<ErrorBoundaryProps> = ({
  children,
  boundaryId,
  errorMessage = "Content temporarily unavailable",
  retryText = "Retry?",
  logPrefix = "Error boundary",
  errorMessageStyle,
  FallbackComponent: CustomFallbackComponent,
}) => {
  const [retryCount, setRetryCount] = useState(0);

  const handleError = useCallback(
    (error: Error, _errorInfo: React.ErrorInfo) => {
      const isDOMManipulationError = error instanceof DOMException;

      // We can't use logger here because it's MDS
      // TODO: plug-inlogger down to MDS
      console.error({
        message: `${logPrefix} triggered`,
        info: {
          errorName: error.name,
          errorMessage: error.message,
          isDOMManipulationError,
          boundaryId,
          retryCount,
        },
      });
    },
    [boundaryId, retryCount, logPrefix]
  );

  const handleReset = useCallback(() => {
    setRetryCount(0);

    console.info({ message: `${logPrefix} manually reset`, info: { boundaryId } });
  }, [boundaryId, logPrefix]);

  return (
    <ErrorBoundary
      FallbackComponent={
        CustomFallbackComponent ||
        (props => (
          <GenericErrorFallback
            {...props}
            errorMessage={errorMessage}
            retryText={retryText}
            logPrefix={logPrefix}
            style={errorMessageStyle}
          />
        ))
      }
      onError={handleError}
      onReset={handleReset}
      resetKeys={[boundaryId]}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * Generic error fallback component that displays a simple error message with retry link
 */
const GenericErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
  errorMessage: string;
  retryText: string;
  logPrefix: string;
  style?: React.CSSProperties;
}> = ({ resetErrorBoundary, errorMessage, retryText, style }) => {
  return (
    <StyledErrorMessage style={{ ...style }}>
      {errorMessage} <StyledRetryLink onClick={resetErrorBoundary}>{retryText}</StyledRetryLink>
    </StyledErrorMessage>
  );
};

const StyledErrorMessage = styled.div<{ style?: React.CSSProperties }>`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-sm"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.editor[500]};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
  margin: ${({ theme }) => theme.spacing[2]} 0;
  pointer-events: auto;
  min-height: 24px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledRetryLink = styled(MDSLink)`
  color: ${({ theme }) => theme.colors.editor[500]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  text-decoration: none;
  pointer-events: auto;

  &:hover:not([data-disabled]) {
    color: ${({ theme }) => theme.colors.editor[500]};
    text-decoration: underline;
  }
`;
